import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { NavigationService, UrlService, WINDOW } from '@frontend/vanilla/core';
import { findLast } from 'lodash-es';
import { Observable, Subject, filter } from 'rxjs';

import { NavigationStack } from './navigation-stack';

export interface StackHistoryItem {
    url: string;
    stack: NavigationStack;
}

@Injectable({
    providedIn: 'root',
})
export class NavigationStackHistoryService {
    private _activeStack?: NavigationStack;
    private history: StackHistoryItem[] = [];
    private currentIndex = 0;
    private _historyChange$ = new Subject<StackHistoryItem>();

    private readonly router = inject(Router);
    private readonly productUtils = inject(SportsProductUtils);
    private readonly destroyRef = inject(DestroyRef);
    private readonly urlService = inject(UrlService);
    private readonly navigationService = inject(NavigationService);
    private readonly window = inject(WINDOW);

    public get historyChange$(): Observable<StackHistoryItem> {
        return this._historyChange$;
    }

    public init(initialUrl: string, initialStack: NavigationStack): void {
        const firstInitialization = this.history.length === 0;
        if (firstInitialization) {
            this.router.events
                .pipe(
                    this.productUtils.filterSports(),
                    filter((x) => x instanceof NavigationEnd),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe((x) => this.processRouterEvent(x));
        }

        this.history = [{ url: initialUrl, stack: initialStack }];
        this._activeStack = initialStack;
        this._activeStack.history = [initialUrl];
    }

    public get activeStack(): NavigationStack | undefined {
        return this._activeStack;
    }

    public set activeStack(currentStack: NavigationStack) {
        this.ensureCurrentUrlIsActual(this.urlService.current().url());
        this._activeStack = currentStack;
    }

    public goBackStackScoped(): void {
        this.ensureCurrentUrlIsActual(this.urlService.current().url());

        if (this.previous?.stack?.name === this.current.stack.name) {
            this.window.history.back();
            return;
        }

        const previousUrlInCurrentStack = findLast(this.current.stack.history, (x) => x !== this.current.url);
        if (!previousUrlInCurrentStack) {
            this.current.stack.history = [];
            this.navigationService.goTo(this.current.stack.rootUrl, { replace: true });
            return;
        }

        const previousUrlIndex = this.current.stack.history.lastIndexOf(previousUrlInCurrentStack);
        this.current.stack.history.splice(previousUrlIndex + 1);
        this.navigationService.goTo(previousUrlInCurrentStack, { replace: true });
    }

    private get current(): StackHistoryItem {
        return this.history[this.currentIndex];
    }

    private get previous(): StackHistoryItem | null {
        if (this.currentIndex <= 0) {
            return null;
        }
        return this.history[this.currentIndex - 1];
    }

    private get next(): StackHistoryItem | null {
        if (this.currentIndex >= this.history.length) {
            return null;
        }
        return this.history[this.currentIndex + 1];
    }

    private processRouterEvent(event: NavigationEnd): void {
        const navState = this.router.getCurrentNavigation();
        const browserInitiatedNav = navState?.trigger === 'popstate';
        const replaceCurrent = !!navState?.extras.replaceUrl;

        this.onUrlChange(event.url, browserInitiatedNav, replaceCurrent);
    }

    private onUrlChange(url: string, browserInitiatedNav: boolean, replaceCurrent: boolean): void {
        if (!this.activeStack) {
            return;
        }

        if (browserInitiatedNav) {
            this.processBrowserInitiatedUrlChange(url);
            return;
        }

        this.activeStack.currentUrl = url;

        if (replaceCurrent) {
            this.current.url = url;
            if (this.current.stack.history.length > 0) {
                this.current.stack.history[this.current.stack.history.length - 1] = url;
            }
        } else {
            this.history.splice(this.currentIndex + 1);

            this.history.push({ url, stack: this.activeStack });
            if (this.activeStack.history.at(-1) !== url) {
                this.activeStack.history.push(url);
            }

            this.currentIndex = this.history.length - 1;
        }

        this._historyChange$.next({ ...this.current });
    }

    private processBrowserInitiatedUrlChange(url: string) {
        if (url === this.next?.url) {
            this.currentIndex++;
        } else if (url === this.previous?.url) {
            this.currentIndex--;
        }

        this._activeStack = this.current.stack;
        this._historyChange$.next({ ...this.current });
    }

    // For cases when url was updated silently without firing a router event
    // We can't use it on urlChange, because the state can be inconsistent with browser url
    private ensureCurrentUrlIsActual(url: string): void {
        if (!this.current || !!this.router.getCurrentNavigation()) {
            return;
        }

        this.current.url = url;
        this.current.stack.currentUrl = url;
        if (this.current.stack.history.length > 0) {
            this.current.stack.history[this.current.stack.history.length - 1] = url;
        }
    }
}
